import React from "react";
import {
  FaRoute,
  FaUserAlt,
  FaPeopleCarry,
  FaTruck,
  FaMapMarkerAlt,
  FaArrowDown,
  FaRegClock,
  FaMoneyBillWave,
  FaRegDotCircle,
} from "react-icons/fa";

export default function RouteCard({ route }) {
  if (!route) return null;

  const {
    id,
    route_number,
    route_status,
    driver,
    loader1,
    loader2,
    carNumber,
    receipts = [],
    fromAddress,
    toAddress,
    currentStatus,
    timeSpent,
    createdAt,
  } = route;

  const total = receipts.length;
  const delivered = receipts.filter(r => r.status === "Закрыта").length;
  const undelivered = receipts.filter(r => r.status === "Не доставлено").length;
  const deliveredPercent = total ? (delivered / total) * 100 : 0;
  const undeliveredPercent = total ? (undelivered / total) * 100 : 0;

  const totalToPay = receipts.reduce((sum, r) => sum + (Number(r.to_pay) || 0), 0);
  const totalPaid = receipts.reduce((sum, r) => sum + (Number(r.paid) || 0), 0);

  const statusColors = {
    Закрыт: { background: "#e8f5e9", color: "#2e7d32" },
    "В процессе": { background: "#fff8e1", color: "#f57c00" },
    Открыт: { background: "#e3f2fd", color: "#1976d2" },
  };
  const statusStyle = statusColors[route_status] || {
    background: "#f0f0f0",
    color: "#555",
  };

  const iconSize = 15;
  const iconColor = "var(--brand)";

  const formatDateTime = dt => {
    if (!dt) return "-";
    try {
      const date = new Date(dt);
      return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dt;
    }
  };

  // === Цвет маркера по статусу квитанции ===
  const getMarkerColor = status => {
    switch (status) {
      case "Закрыта":
        return "#00C853"; // зелёный
      case "Не доставлено":
        return "#E53935"; // красный
      case "Открыта":
        return "#007DFF"; // синий
      default:
        return "#9E9E9E"; // серый
    }
  };

  // === Поиск статусов адресов (если есть квитанции) ===
  const findReceiptStatus = address => {
    const r = receipts.find(x => x.delivery_address === address);
    return r?.status || null;
  };

  const fromStatus = findReceiptStatus(fromAddress);
  const toStatus = findReceiptStatus(toAddress);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "0.6rem",
        boxShadow: "0 1px 5px rgba(0,0,0,0.05)",
        padding: "1rem 1.2rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "260px",
        boxSizing: "border-box",
        transition: "all 0.2s ease",
      }}
    >
      {/* === Верх === */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <FaRoute size={iconSize} color={iconColor} />
          <b style={{ fontSize: "0.95rem", color: "var(--text)" }}>
            Маршрут №{route_number || id}
          </b>
        </div>
        <span
          style={{
            fontSize: "0.7rem",
            fontWeight: 600,
            padding: "0.25rem 0.55rem",
            borderRadius: "0.4rem",
            textTransform: "uppercase",
            ...statusStyle,
          }}
        >
          {route_status}
        </span>
      </div>

      {/* === Основная информация === */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", marginTop: "0.3rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <FaUserAlt size={iconSize} color={iconColor} />
          <span style={{ fontSize: "0.85rem" }}>{driver || "—"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <FaPeopleCarry size={iconSize} color={iconColor} />
          <span style={{ fontSize: "0.85rem" }}>
            {[loader1, loader2].filter(Boolean).join(", ") || "—"}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <FaTruck size={iconSize} color={iconColor} />
          <span style={{ fontSize: "0.85rem" }}>{carNumber || "—"}</span>
        </div>
      </div>

      {/* === Состояние маршрута === */}
      <div
        style={{
          background: "var(--brand-light)",
          borderRadius: "0.5rem",
          padding: "0.7rem 0.9rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginTop: "0.5rem",
        }}
      >
        {/* Статус + время */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            fontSize: "0.85rem",
            fontWeight: 600,
            color: currentStatus === "В пути" ? "#1565C0" : "#2E7D32",
          }}
        >
          {currentStatus === "В пути" ? (
            <FaArrowDown size={13} color="#1565C0" />
          ) : (
            <FaRegDotCircle size={13} color="#2E7D32" />
          )}
          <span>
            {currentStatus || "—"}{" "}
            {timeSpent && (
              <span style={{ fontWeight: 400, color: "var(--muted)" }}>• {timeSpent}</span>
            )}
          </span>
        </div>

        {/* === Адреса === */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.3rem",
            height: "48px",
            overflow: "hidden",
          }}
        >
          {currentStatus === "Стоит на точке" && (
            <div
              title={fromAddress}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <FaMapMarkerAlt size={iconSize} color={getMarkerColor(fromStatus)} />
              <span style={{ fontSize: "0.8rem" }}>{fromAddress}</span>
            </div>
          )}

          {currentStatus === "В пути" && (
            <>
              <div
                title={fromAddress}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <FaMapMarkerAlt size={iconSize} color={getMarkerColor(fromStatus)} />
                <span style={{ fontSize: "0.8rem" }}>{fromAddress}</span>
              </div>

              <div
                title={toAddress}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <FaMapMarkerAlt size={iconSize} color={getMarkerColor(toStatus)} />
                <span style={{ fontSize: "0.8rem" }}>{toAddress}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* === Прогресс === */}
      <div style={{ marginTop: "0.4rem" }}>
        <div
          style={{
            height: "8px",
            background: "#eee",
            borderRadius: "4px",
            overflow: "hidden",
            display: "flex",
          }}
        >
          <div
            style={{
              width: `${deliveredPercent}%`,
              background: "#00C853",
              transition: "width 0.3s ease",
            }}
          />
          <div
            style={{
              width: `${undeliveredPercent}%`,
              background: "#E53935",
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>

      {/* === Нижняя панель === */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.3rem",
          fontSize: "0.8rem",
          color: "var(--muted)",
          borderTop: "1px solid #f0f0f0",
          paddingTop: "0.5rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
          <FaMoneyBillWave size={iconSize} color={iconColor} />
          <span>
            {totalPaid.toLocaleString()} ₽ / {totalToPay.toLocaleString()} ₽
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
          <FaRegClock size={iconSize} color={iconColor} />
          <span>{formatDateTime(createdAt)}</span>
        </div>
      </div>
    </div>
  );
}
